/**
 * Google Business sign-in through one fixed callback page.
 *
 * Google only redirects to pages registered in Google Cloud Console, and partner
 * dashboard hosts cannot be registered one by one. So Shop sends every manager back to
 * a single callback page on the neutral white-label host, served by this same build.
 * That page relays Google's answer to Shop and reports the result to the dashboard that
 * opened the popup with `postMessage`; the dashboard also polls, in case the message
 * never arrives.
 */

export const GOOGLE_BUSINESS_OAUTH_CALLBACK_PATH = '/oauth/google-business/callback';

export const GOOGLE_BUSINESS_OAUTH_MESSAGE_TYPE = 'gbp-oauth-result';

export const GOOGLE_BUSINESS_OAUTH_POPUP_NAME = 'gbp-oauth';

export const GOOGLE_BUSINESS_OAUTH_POPUP_FEATURES = 'width=520,height=640';

export const GOOGLE_BUSINESS_OAUTH_POLL_INTERVAL_MS = 2000;

/** Matches the ten minutes Shop keeps the sign-in, with room to spare. */
export const GOOGLE_BUSINESS_OAUTH_TIMEOUT_MS = 5 * 60 * 1000;

export const GoogleBusinessOAuthStatus = Object.freeze({
  Connected: 'connected',
  Failed: 'failed',
  Cancelled: 'cancelled',
  // Opener-side only: the popup went away, or nothing happened in time.
  Closed: 'closed',
  TimedOut: 'timed_out',
});

export function isGoogleBusinessOAuthCallback(pathname) {
  return pathname === GOOGLE_BUSINESS_OAUTH_CALLBACK_PATH;
}

/**
 * Callback page: relays Google's query to Shop and tells the opener how it went.
 *
 * `complete` is the Shop call (normalized `{ success, data }`). The result only goes to
 * the origin Shop returns, which it checked when the sign-in started, never to `'*'`.
 *
 * @returns {Promise<{ status: string, errorCode: string|null, reported: boolean }>}
 */
export async function completeGoogleBusinessOAuth({ search, opener, complete }) {
  const params = new URLSearchParams(search);
  const { success, data } = await complete({
    state: params.get('state'),
    code: params.get('code'),
    error: params.get('error'),
  });
  const result = success ? data?.data : null;

  if (!result?.opener_origin) {
    return { status: GoogleBusinessOAuthStatus.Failed, errorCode: data?.error_code ?? null, reported: false };
  }

  const errorCode = result.error_code ?? null;

  if (!opener) {
    return { status: result.status, errorCode, reported: false };
  }

  opener.postMessage(
    { type: GOOGLE_BUSINESS_OAUTH_MESSAGE_TYPE, status: result.status, error_code: errorCode },
    result.opener_origin,
  );

  return { status: result.status, errorCode, reported: true };
}

/**
 * Opener: waits for the sign-in running in `popup` to end. The first of these wins:
 *
 * - the callback page posts the result, accepted only from `callbackOrigin`;
 * - `isConnected()`, polled every `pollIntervalMs`, says the profile is connected —
 *   the fallback for a message that never arrives;
 * - the popup is closed, after one last `isConnected()`;
 * - `timeoutMs` pass — a hard deadline, so a status request that never answers cannot
 *   keep the wait (and the dashboard's spinner) alive.
 *
 * @returns {{ promise: Promise<{ status: string, errorCode: string|null }>, cancel: () => void }}
 */
export function waitForGoogleBusinessOAuth({
  popup,
  callbackOrigin,
  isConnected,
  target = window,
  pollIntervalMs = GOOGLE_BUSINESS_OAUTH_POLL_INTERVAL_MS,
  timeoutMs = GOOGLE_BUSINESS_OAUTH_TIMEOUT_MS,
}) {
  let finished = false;
  let checking = false;
  let timer = null;
  let deadline = null;
  let resolveResult;

  const promise = new Promise((resolve) => {
    resolveResult = resolve;
  });

  function finish(status, errorCode = null) {
    if (finished) {
      return;
    }

    finished = true;
    target.removeEventListener('message', onMessage);
    clearInterval(timer);
    clearTimeout(deadline);
    resolveResult({ status, errorCode });
  }

  function onMessage(event) {
    if (event.origin !== callbackOrigin || event.data?.type !== GOOGLE_BUSINESS_OAUTH_MESSAGE_TYPE) {
      return;
    }

    finish(event.data.status, event.data.error_code ?? null);
  }

  async function check() {
    if (checking || finished) {
      return;
    }

    checking = true;
    // Read before asking: a popup that closed while the request ran still gets its
    // answer from this check, and only then ends the wait.
    const closed = popup.closed;
    const connected = await isConnected().catch(() => false);
    checking = false;

    if (connected) {
      return finish(GoogleBusinessOAuthStatus.Connected);
    }

    if (closed) {
      finish(GoogleBusinessOAuthStatus.Closed);
    }
  }

  target.addEventListener('message', onMessage);
  timer = setInterval(check, pollIntervalMs);
  deadline = setTimeout(() => finish(GoogleBusinessOAuthStatus.TimedOut), timeoutMs);

  return { promise, cancel: () => finish(GoogleBusinessOAuthStatus.Closed) };
}
